import { onSnapshot } from 'firebase/firestore';

const PERMISSION_DENIED = 'permission-denied';

/**
 * onSnapshot wrapper that transparently recovers from transient
 * `permission-denied` errors.
 *
 * Right after a user joins a family, a member-gated subcollection listener can
 * be briefly denied: the joining user's user-doc `familyIds` update reaches the
 * client before the family-doc membership write is visible to the security
 * rules' `get(families/{id})` read (the two propagate independently, so the
 * rules' read can lag the client's own family-doc snapshot). A denied
 * onSnapshot listener is terminal — it would otherwise stay dead until the
 * component remounts.
 *
 * This wrapper silently resubscribes a few times with exponential backoff when
 * it sees `permission-denied`. A successful snapshot resets the budget. Only a
 * denial that persists past the retries (e.g. a genuine non-member) is
 * forwarded to `onError`, so real permission problems still surface and there
 * is no unbounded retry storm.
 *
 * Mirrors the `onSnapshot(ref, onNext, onError)` signature, so it is a drop-in
 * replacement at the call sites (the services import it aliased as
 * `onSnapshot`).
 *
 * @param {import('firebase/firestore').Query|import('firebase/firestore').DocumentReference} ref
 * @param {(snapshot: any) => void} onNext
 * @param {(error: any) => void} [onError]
 * @param {{ retries?: number, baseDelayMs?: number }} [options]
 * @returns {() => void} unsubscribe
 */
export const onSnapshotWithRetry = (ref, onNext, onError, options = {}) => {
  const { retries = 4, baseDelayMs = 350 } = options;
  let unsub = null;
  let timer = null;
  let attempt = 0;
  let cancelled = false;

  const subscribe = () => {
    if (cancelled) return;
    unsub = onSnapshot(
      ref,
      (snapshot) => {
        attempt = 0; // a successful read clears the retry budget
        onNext(snapshot);
      },
      (error) => {
        if (error?.code === PERMISSION_DENIED && attempt < retries && !cancelled) {
          attempt += 1;
          if (unsub) { unsub(); unsub = null; }
          timer = setTimeout(subscribe, baseDelayMs * 2 ** (attempt - 1));
          return;
        }
        if (onError) onError(error);
      }
    );
  };

  subscribe();

  return () => {
    cancelled = true;
    if (timer) { clearTimeout(timer); timer = null; }
    if (unsub) { unsub(); unsub = null; }
  };
};
